import { useState, useEffect } from "react";
import { MapPin, BedDouble, Maximize, IndianRupee, Shield, Globe, Plus, Phone, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImageUpload from "@/components/ImageUpload";
import AIListingHelper from "@/components/AIListingHelper";
import FeaturedListings from "@/components/FeaturedListings";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";

const RealEstatePage = () => {
  const [properties, setProperties] = useState([]);
  const [scrapedProperties, setScrapedProperties] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [filter, setFilter] = useState("all");
  const [locFilter, setLocFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title:"", property_type:"", category:"", location:"", price:"", bedrooms:"", area_sqft:"", contact:"", description:"", image_url:"", is_nri:false });

  useEffect(() => {
    axios.get(`${API}/properties`).then(r => setProperties(r.data)).catch(console.error);
    axios.get(`${API}/scraped-properties`).then(r => setScrapedProperties(r.data)).catch(console.error);
  }, []);

  const handleScrape = async (location = "kokapet") => {
    setScraping(true);
    setScrapeResult(null);
    try {
      const res = await axios.post(`${API}/scrape/properties`, { location, property_type: "rent", limit: 10 });
      setScrapeResult(res.data);
      setScrapedProperties(res.data.results || []);
    } catch { setScrapeResult({ error: "Scraping failed. Sites may be blocking requests." }); }
    setScraping(false);
  };

  const filtered = properties.filter(p => (filter === "all" || p.property_type === filter) && (locFilter === "all" || p.location.toLowerCase() === locFilter.toLowerCase()));
  const getWA = (p) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I'm interested in "${p.title}" at ${p.location} for ${p.price} on Junction.`)}`;
  const typeColors = { rent: "bg-green-50 text-green-700", sell: "bg-blue-50 text-blue-700" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.property_type || !form.location || !form.price || !form.contact) { toast.error("Fill required fields"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/properties`, { ...form, bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null, area_sqft: form.area_sqft ? parseInt(form.area_sqft) : null });
      setProperties(prev => [res.data, ...prev]);
      toast.success("Property listed!");
      setForm({ title:"", property_type:"", category:"", location:"", price:"", bedrooms:"", area_sqft:"", contact:"", description:"", image_url:"", is_nri:false });
      setDialogOpen(false);
    } catch { toast.error("Failed."); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-sage/5 border-b border-sage/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-indigo mb-1">Flats & Villas — Zero Brokerage</h1>
          <p className="text-sm text-gray-500">Rent, Buy & Sell properties in your neighborhood. NRI field agent services available.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* NRI Callout */}
        <div className="bg-indigo rounded-2xl p-6 mb-8 flex flex-col md:flex-row md:items-center gap-4" data-testid="nri-services-callout">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1"><Globe size={14} className="text-marigold" /><span className="text-[10px] font-bold text-marigold uppercase tracking-widest">NRI Services</span></div>
            <h3 className="font-heading text-lg font-medium text-white mb-1">Managing property from abroad?</h3>
            <p className="text-white/60 text-xs">Field agents for visits, screening, agreements. Transparent flat fees.</p>
          </div>
          <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi, I'm an NRI interested in Junction's property services.")}`} target="_blank" rel="noopener noreferrer">
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" data-testid="nri-whatsapp-button"><MessageCircle size={14} className="mr-1.5" />WhatsApp Us</Button>
          </a>
        </div>

        {/* Filters */}
        <div id="post" className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex gap-2">
            {["all","rent","sell"].map(t => (
              <button key={t} onClick={() => setFilter(t)} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${filter===t ? "bg-indigo text-white" : "bg-white text-gray-500 border border-gray-200 hover:border-indigo"}`} data-testid={`filter-${t}`}>{t==="all" ? "All" : t==="rent" ? "For Rent" : "For Sale"}</button>
            ))}
          </div>
          <Select value={locFilter} onValueChange={setLocFilter}>
            <SelectTrigger className="w-40 rounded-xl h-9 text-xs border-gray-200"><SelectValue placeholder="All Areas" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Areas</SelectItem>{["Gachibowli","Madhapur","Kokapet","Narsingi","Gandipet","Kondapur","Kukatpally"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <div className="ml-auto">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-full px-4 text-xs border-sage text-sage hover:bg-sage hover:text-white h-9" data-testid="post-property-button"><Plus size={14} className="mr-1" />List Property</Button></DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="property-dialog">
                <DialogHeader><DialogTitle className="font-heading text-lg text-indigo">List Your Property</DialogTitle><DialogDescription>No brokerage. Reach genuine buyers & tenants.</DialogDescription></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                  <Input placeholder="Title *" value={form.title} onChange={e => setForm({...form, title:e.target.value})} className="rounded-xl" data-testid="property-title-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={form.property_type} onValueChange={v => setForm({...form, property_type:v})}><SelectTrigger className="rounded-xl" data-testid="property-type-select"><SelectValue placeholder="Type *" /></SelectTrigger><SelectContent><SelectItem value="rent">Rent</SelectItem><SelectItem value="sell">Sale</SelectItem></SelectContent></Select>
                    <Select value={form.category} onValueChange={v => setForm({...form, category:v})}><SelectTrigger className="rounded-xl" data-testid="property-category-select"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="flat">Flat</SelectItem><SelectItem value="villa">Villa</SelectItem><SelectItem value="plot">Plot</SelectItem></SelectContent></Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Location *" value={form.location} onChange={e => setForm({...form, location:e.target.value})} className="rounded-xl" data-testid="property-location-input" />
                    <Input placeholder="Price *" value={form.price} onChange={e => setForm({...form, price:e.target.value})} className="rounded-xl" data-testid="property-price-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" placeholder="Bedrooms" value={form.bedrooms} onChange={e => setForm({...form, bedrooms:e.target.value})} className="rounded-xl" data-testid="property-bedrooms-input" />
                    <Input type="number" placeholder="Sqft" value={form.area_sqft} onChange={e => setForm({...form, area_sqft:e.target.value})} className="rounded-xl" data-testid="property-area-input" />
                  </div>
                  <Input placeholder="Contact *" value={form.contact} onChange={e => setForm({...form, contact:e.target.value})} className="rounded-xl" data-testid="property-contact-input" />
                  <ImageUpload value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} />
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Description</label>
                      <AIListingHelper listingType="property" details={{title: form.title, type: form.property_type, category: form.category, location: form.location, price: form.price, bedrooms: form.bedrooms}} onGenerated={(d) => setForm({...form, description: d})} />
                    </div>
                    <Textarea placeholder="Description (or click AI Write)" value={form.description} onChange={e => setForm({...form, description:e.target.value})} className="rounded-xl min-h-[60px]" data-testid="property-description-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-indigo text-white rounded-full" data-testid="property-submit-button">{submitting ? "Posting..." : "List Property"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* ─── Featured Listings ─── */}
        <div className="mb-8" data-testid="featured-section">
          <FeaturedListings />
        </div>

        {/* ─── AI Property Discovery ─── */}
        <div className="bg-indigo/5 rounded-2xl p-5 mb-6 border border-indigo/10" data-testid="scraper-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-marigold" />
                <h3 className="font-heading text-sm font-semibold text-indigo">AI Property Discovery</h3>
              </div>
              <p className="text-[11px] text-gray-500">Auto-find rental flats from 99acres, MagicBricks, Housing.com & OLX</p>
            </div>
            <div className="flex gap-2">
              {["Kokapet", "Gandipet", "Narsingi", "Gachibowli", "Kondapur"].map(loc => (
                <Button key={loc} variant="outline" size="sm" onClick={() => handleScrape(loc)} disabled={scraping} className="rounded-full text-[11px] h-7 border-indigo/20 text-indigo hover:bg-indigo hover:text-white" data-testid={`scrape-${loc.toLowerCase()}`}>
                  {scraping ? "..." : loc}
                </Button>
              ))}
            </div>
          </div>

          {scrapeResult && !scrapeResult.error && (
            <div className="mt-3">
              <div className="flex items-center gap-3 mb-2 text-[11px] text-gray-500">
                <span>Found <strong className="text-indigo">{scrapeResult.total_found}</strong> listings in {scrapeResult.location}</span>
                {Object.entries(scrapeResult.sources || {}).map(([name, s]) => (
                  <span key={name} className={`px-2 py-0.5 rounded-full ${s.count > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"}`}>{name}: {s.count}</span>
                ))}
              </div>
            </div>
          )}
          {scrapeResult?.error && <p className="text-xs text-terracotta mt-2">{scrapeResult.error}</p>}

          {scrapedProperties.length > 0 && (
            <div className="mt-3 space-y-2">
              {scrapedProperties.slice(0, 8).map((sp, i) => (
                <div key={i} className="bg-white rounded-xl p-3 flex items-center justify-between border border-gray-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-indigo truncate">{sp.title}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400">
                      <span>{sp.location}</span>
                      {sp.price && <span className="font-medium text-indigo">{sp.price}</span>}
                      <Badge className="rounded-full text-[9px] bg-gray-100 text-gray-500">{sp.source}</Badge>
                    </div>
                  </div>
                  {sp.url && (
                    <a href={sp.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-6 px-2 border-gray-200 text-indigo">View</Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mb-4">{filtered.length} properties found</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="real-estate-section">
          {filtered.map(p => (
            <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-md transition-shadow" data-testid={`property-card-${p.id}`}>
              {p.image_url && <div className="h-32 overflow-hidden"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></div>}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge className={`rounded-full text-[11px] ${typeColors[p.property_type] || "bg-gray-50 text-gray-600"}`}>{p.property_type==="rent" ? "Rent" : "Sale"}</Badge>
                  {p.is_nri && <Badge variant="outline" className="rounded-full text-[10px] border-marigold text-marigold">NRI</Badge>}
                </div>
                <h4 className="font-medium text-indigo text-sm mb-1 line-clamp-2">{p.title}</h4>
                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-2"><MapPin size={10} />{p.location}</div>
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3">
                  {p.bedrooms && <span className="flex items-center gap-1"><BedDouble size={10} />{p.bedrooms}BHK</span>}
                  {p.area_sqft && <span className="flex items-center gap-1"><Maximize size={10} />{p.area_sqft}sqft</span>}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <span className="font-heading font-semibold text-indigo text-sm"><IndianRupee size={11} className="inline" />{p.price}</span>
                  <div className="flex gap-1">
                    <a href={`tel:${p.contact}`}><Button variant="outline" size="sm" className="rounded-lg text-[11px] px-2 h-7 border-gray-200 text-indigo hover:bg-indigo hover:text-white" data-testid={`property-call-${p.id}`}><Phone size={10} /></Button></a>
                    <a href={getWA(p)} target="_blank" rel="noopener noreferrer"><Button size="sm" className="rounded-lg text-[11px] px-2 h-7 bg-green-600 hover:bg-green-700 text-white" data-testid={`property-whatsapp-${p.id}`}><MessageCircle size={10} /></Button></a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center py-16 text-gray-400 text-sm" data-testid="no-properties-message">No properties found.</p>}
      </div>
      <Footer />
    </div>
  );
};

export default RealEstatePage;
